import React, { useCallback, useEffect, useState } from 'react';
import { Container } from '@mui/system';
import { MetaTags } from '../../components/Metatags/Metatags';
import { FeedContainer } from '../../components/ScreenContainer/FeedContainer';
import { useDispatch } from 'react-redux';
import { getActivityFeed, getFeedSuggestion } from '../../redux/activity/actions';
import { useSelector } from 'react-redux';
import { activityFeedSelector, bookmarkingSelector, feedSuggestionSelector, filterSelector } from './selector';
import { getJWtDetails, showToastMessage } from '../../utils/AppUtils';
import { isEmpty } from '../../utils/lodash';
import moment from 'moment';
import { createBookmark, deleteBookmark } from '../../redux/contest/actions';
import { TBookmarkTypes } from '../../redux/contest/types';
import { setRecommendation } from '../../redux/appConfigurator/action';
import { replaceRouteParam } from '../../utils/routesProcessor';
import { ROUTES } from '../../types/global/routes.types';
import { IAudition } from '../../types/global/helper';
import { navigate } from '../../redux/navigator/action';
import { FeedSubresource } from '../../redux/activity/types';
import { feedFilter } from '../../data/filters/feed.filter';

const DAYS_TO_FETCH = 30;
const Feed = () => {
    const dispatch = useDispatch();
    const { chronological, following, interest } = useSelector(activityFeedSelector) || {};
    const { fetching, filters = feedFilter, fetchingTimeline, nextCursor } = useSelector(filterSelector) || {};
    const [filter, setFilter] = useState<FeedSubresource>('none');

    const bookmarking = useSelector(bookmarkingSelector);
    const feedSuggestion = useSelector(feedSuggestionSelector);

    const {
        canLoadMore: canLoadMoreFollowing,
        data: followingData,
        loading: followingLoading,
        page: followingPage,
        currentPagination: followPagination,
    } = following || {};
    const {
        canLoadMore: canLoadMoreInterest,
        data: interestData,
        loading: interestLoading,
        page: interestPage,
        currentPagination: interestPagination,
    } = interest || {};
    const {
        canLoadMore: canLoadMoreChronological,
        data: chronologicalData,
        loading: chronogicalLoading,
        page: chronologicalPage,
        currentPagination: chronologicalPagination,
    } = chronological || {};
    const { isSignedIn } = getJWtDetails();
    const handleGetFeed = useCallback(
        (activity_type?: FeedSubresource) => {
            dispatch(
                getActivityFeed({
                    category:
                        !isSignedIn || (activity_type && activity_type !== 'none') ? 'chronological' : 'following',
                    startDate: moment().startOf('day').toISOString(),
                    endDate: moment()
                        .subtract(activity_type && activity_type !== 'none' ? 90 : DAYS_TO_FETCH, 'days')
                        .startOf('day')
                        .toISOString(),
                    ...(activity_type && { activity_type: activity_type }),
                    cursor: null, // To Enforce Page 1
                }),
            );
            // if (!feedSuggestion)
            dispatch(getFeedSuggestion());
            // dispatch(getPersistentFeed({ isLoadMore: false }));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isSignedIn],
    );

    const handleOnLeftFeedScrollEndReached = () => {
        if (!fetchingTimeline) {
            // dispatch(getPersistentFeed({ isLoadMore: true }));
            if (canLoadMoreFollowing && !followingLoading && isSignedIn) {
                dispatch(
                    getActivityFeed({
                        category: 'following',
                        page: followingPage + 1,
                        startDate: moment().startOf('day').toISOString(),
                        endDate: moment().subtract(DAYS_TO_FETCH, 'days').startOf('day').toISOString(),
                        ...(filter && filter !== 'none' && { activity_type: filter }),
                        cursor: nextCursor,
                    }),
                );
            }

            if (!canLoadMoreFollowing && canLoadMoreInterest && !interestLoading && isSignedIn && !followingLoading) {
                dispatch(
                    getActivityFeed({
                        category: 'interest',
                        page: interestPage + 1,
                        startDate: moment().startOf('day').toISOString(),
                        endDate: moment().subtract(DAYS_TO_FETCH, 'days').startOf('day').toISOString(),
                        ...(filter && filter !== 'none' && { activity_type: filter }),
                        cursor: nextCursor,
                    }),
                );
            }

            if (
                !canLoadMoreFollowing &&
                canLoadMoreChronological &&
                !interestLoading &&
                !canLoadMoreInterest &&
                !chronogicalLoading &&
                !followingLoading
            ) {
                dispatch(
                    getActivityFeed({
                        category: 'chronological',
                        page: chronologicalPage + 1,
                        show_seen: chronologicalPagination?.show_seen,
                        ...(filter && filter !== 'none' && { activity_type: filter }),
                        cursor: nextCursor,
                    }),
                );
            }
        }
    };
    const handleOnRightFeedScrollEndReached = () => {};

    /** This should work with cursor stacking and using older cursor to call */
    const handleShowOlder = (category: string) => {
        // dispatch(getPersistentFeed({ isLoadMore: true }));
        switch (category) {
            case 'following':
                dispatch(
                    getActivityFeed({
                        category: 'following',
                        page: 1,
                        startDate: followPagination?.endDate,
                        endDate: moment(followPagination?.endDate)
                            .subtract(DAYS_TO_FETCH, 'days')
                            .startOf('day')
                            .toISOString(),
                        ...(filter && filter !== 'none' && { activity_type: filter }),
                        cursor: nextCursor,
                    }),
                );
                break;
            case 'interest':
                dispatch(
                    getActivityFeed({
                        category: 'interest',
                        page: 1,
                        startDate: interestPagination?.endDate,
                        endDate: moment(interestPagination?.endDate)
                            .subtract(DAYS_TO_FETCH, 'days')
                            .startOf('day')
                            .toISOString(),
                        ...(filter && filter !== 'none' && { activity_type: filter }),
                        cursor: nextCursor,
                    }),
                );
                break;
            case 'chronological':
                dispatch(
                    getActivityFeed({
                        category: 'chronological',
                        page: 1,
                        startDate: chronologicalPagination?.endDate,
                        endDate: moment(chronologicalPagination?.endDate)
                            .subtract(DAYS_TO_FETCH, 'days')
                            .startOf('day')
                            .toISOString(),
                        ...(filter && filter !== 'none' && { activity_type: filter }),
                        cursor: nextCursor,
                    }),
                );
                break;
            default:
                dispatch(
                    getActivityFeed({
                        category: 'chronological',
                        page: 1,
                        startDate: chronologicalPagination?.endDate,
                        endDate: moment(chronologicalPagination?.endDate)
                            .subtract(DAYS_TO_FETCH, 'days')
                            .startOf('day')
                            .toISOString(),
                        ...(filter && filter !== 'none' && { activity_type: filter }),
                        cursor: nextCursor,
                    }),
                );
                break;
        }
    };

    useEffect(() => {
        handleGetFeed();
    }, [handleGetFeed]);

    const handleOnBookmarkJob = (collectionType: string, model: any) => {
        if (!bookmarking) {
            let type: TBookmarkTypes = 'Audition';
            switch (collectionType) {
                case 'gig':
                    /** Save audition */
                    type = 'Audition';
                    break;
                case 'event':
                    /** Save Events */
                    type = 'Event';
                    break;
            }

            dispatch(
                createBookmark({
                    details: {
                        model: model._id,
                        type,
                    },
                }),
            );
        } else {
            showToastMessage('Slow down!!!', 'warning');
        }
    };
    const handleOnDeleteBookmark = (collectionType: string, id: any, model: any) => {
        if (!bookmarking) {
            let type: TBookmarkTypes = 'Audition';
            switch (collectionType) {
                case 'gig':
                    /** Save audition */
                    type = 'Audition';
                    break;
                case 'event':
                    /** Save Events */
                    type = 'Event';
                    break;
            }
            dispatch(
                deleteBookmark({
                    documentId: id,
                    morePayload: type,
                    additionalPayload: model,
                }),
            );
        } else {
            showToastMessage('Slow down!!!', 'warning');
        }
    };
    const handleOnRecommendClick = (type: string, model: any) => {
        dispatch(
            setRecommendation({
                type: type as any, // Type this properly
                document: model,
            }),
        );
    };
    const handleOnSubmitClick = (type: string, model: any) => {};
    const handleOnDetailsClick = (type: string, model: IAudition) => {
        if (type === 'gig') {
            const route = replaceRouteParam(ROUTES.AUDITION_DETAIL, 'auditionId', model.slug);
            dispatch(
                navigate({
                    routes: `${route}`,
                    skipAuthentication: true,
                }),
            );
        }
    };

    return (
        <MetaTags title="Feed | Discovery" description="Discover new campaigns, projects and talents">
            <Container
                disableGutters
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    width: '100%',
                    height: '100vh',
                    marginTop: { xs: '10px', sm: '0px', md: '15px', mdLg: '20px', lg: '0px' },
                }}
                maxWidth={false}>
                <FeedContainer
                    onLeftFeedScrollEndReached={handleOnLeftFeedScrollEndReached}
                    onBookmarkJob={handleOnBookmarkJob}
                    onDeleteBookmark={handleOnDeleteBookmark}
                    onRecommendClick={handleOnRecommendClick}
                    onClickFilter={filter => {
                        if (filter === 'none') {
                            handleGetFeed();
                            setFilter('none');
                        } else {
                            handleGetFeed(filter);
                            setFilter(filter);
                        }
                    }}
                    dynamicFilter={filters}
                    loading={fetching}
                    onSubmitClick={handleOnSubmitClick}
                    onRightFeedScrollEndReached={handleOnRightFeedScrollEndReached}
                    endMessageForChronologicalActivity={
                        'You have seen all the activities for the past 7 days on thspian'
                    }
                    endMessageForFollowersActivity="You have seen all recent activities from your circle"
                    endMessageForInterestActivity="You have seen all suggested activities from thspian"
                    onShowOlder={handleShowOlder}
                    onDetailsClick={handleOnDetailsClick}
                    shouldShowEndMessageForChronologicalActivity={
                        !canLoadMoreChronological &&
                        !followingLoading &&
                        !interestLoading &&
                        !canLoadMoreFollowing &&
                        !canLoadMoreInterest &&
                        !chronogicalLoading
                    }
                    shouldShowEndMessageForFollowersActivity={
                        !canLoadMoreFollowing &&
                        canLoadMoreInterest &&
                        !interestLoading &&
                        isSignedIn &&
                        !followingLoading
                    }
                    shouldShowEndMessageForInterestActivity={
                        !canLoadMoreFollowing &&
                        canLoadMoreChronological &&
                        !interestLoading &&
                        !canLoadMoreInterest &&
                        !chronogicalLoading &&
                        !followingLoading
                    }
                    chronologicalActivities={chronologicalData}
                    shouldShowLoading={followingLoading || interestLoading}
                    followingActivities={followingData}
                    interestActivities={interestData}
                    startFollowingLoad={isSignedIn && !isEmpty(followingData)}
                    startInterestLoad={isSignedIn && !canLoadMoreFollowing && !isEmpty(interestData)}
                    startChronologicalLoad={!isEmpty(chronologicalData)}
                    showChronogicalLoading={chronogicalLoading}
                    suggestion={feedSuggestion}
                    onJoinCampaign={function (type: string, model: any): void {
                        throw new Error('Function not implemented.');
                    }}
                    onRegister={function (type: string, model: any): void {
                        throw new Error('Function not implemented.');
                    }}
                    onCommentClick={() => {}}
                    onLikeClick={() => {}}
                    onViewClick={() => {}}
                />
            </Container>
        </MetaTags>
    );
};

export { Feed };
